/**
 * audit.js — who did what, described in a sentence.
 *
 * WHY THIS EXISTS
 * v1 had no activity trail at all, and it also had no working team management,
 * so the two absences hid each other. With more than one user the question
 * "who cancelled INV/2026-27/0031?" has to have an answer, and the ledgers
 * cannot give it: PartyLedger records that the amount was reversed, not who
 * reversed it.
 *
 * WHAT IS AND IS NOT LOGGED
 * Only actions with a consequence somebody would want to trace: a document
 * created or cancelled, money recorded, stock adjusted, a price list changed,
 * a person's role changed. Reads are not logged — a log that records every
 * screen open is a log nobody reads, and Firestore charges a write for each
 * one. Nothing here stores a phone number, a GSTIN or a customer address: the
 * log is about the ACTION, and the document it names can be opened by anyone
 * who is entitled to see those details anyway.
 *
 * Pure: every function takes plain data and returns plain data.
 */

export const ACTIVITY_TYPES = {
  VOUCHER_CREATED: 'voucher.created',
  VOUCHER_CANCELLED: 'voucher.cancelled',
  PAYMENT_RECORDED: 'payment.recorded',
  ACCOUNT_ENTRY: 'account.entry',
  ACCOUNT_TRANSFER: 'account.transfer',
  CHEQUE_CLEARED: 'cheque.cleared',
  CHEQUE_BOUNCED: 'cheque.bounced',
  STOCK_ADJUSTED: 'stock.adjusted',
  STOCK_TRANSFERRED: 'stock.transferred',
  STOCK_TAKE: 'stock.take',
  PRICE_LIST_CHANGED: 'pricelist.changed',
  MEMBER_INVITED: 'member.invited',
  MEMBER_ROLE_CHANGED: 'member.role',
  MEMBER_REMOVED: 'member.removed',
  SETTINGS_CHANGED: 'settings.changed',
  RETURN_FILED: 'return.filed',

  /**
   * The general ledger.
   *
   * `ACC_VOUCHER_EDITED` is the one that earns its place here. Every other
   * event in this list records something being created or removed; an EDIT
   * changes a figure that has already been read, reported and possibly filed,
   * and "who changed the amount, and from what?" is the question an audit
   * actually asks. The `detail` carries the field-level summary — see
   * lib/voucher-diff.
   */
  ACC_VOUCHER_POSTED: 'accounting.posted',
  ACC_VOUCHER_EDITED: 'accounting.edited',
  ACC_VOUCHER_CANCELLED: 'accounting.cancelled',
  PERIOD_LOCKED: 'accounting.period.locked',
  PERIOD_REOPENED: 'accounting.period.reopened',
};

/**
 * How each event reads in a list, and how alarming it should look.
 *
 * `tone` is not decoration. A cancelled invoice and a created one are the same
 * size in a list of forty rows, and the one worth noticing is the cancellation.
 */
const DESCRIPTORS = {
  [ACTIVITY_TYPES.VOUCHER_CREATED]: {
    label: 'Document created',
    tone: 'neutral',
    sentence: (e) => `Created ${e.subjectLabel || 'a document'}${e.partyName ? ` for ${e.partyName}` : ''}.`,
  },
  [ACTIVITY_TYPES.VOUCHER_CANCELLED]: {
    label: 'Document cancelled',
    tone: 'danger',
    sentence: (e) => `Cancelled ${e.subjectLabel || 'a document'}${e.reason ? ` — ${e.reason}` : ''}.`,
  },
  [ACTIVITY_TYPES.PAYMENT_RECORDED]: {
    label: 'Payment recorded',
    tone: 'success',
    sentence: (e) => `Recorded a payment${e.subjectLabel ? ` against ${e.subjectLabel}` : ''}.`,
  },
  [ACTIVITY_TYPES.ACCOUNT_ENTRY]: {
    label: 'Money in or out',
    tone: 'neutral',
    sentence: (e) => `${e.detail || 'Recorded an entry'} in ${e.subjectLabel || 'an account'}.`,
  },
  [ACTIVITY_TYPES.ACCOUNT_TRANSFER]: {
    label: 'Transfer',
    tone: 'neutral',
    sentence: (e) => `Transferred money ${e.detail || 'between accounts'}.`,
  },
  [ACTIVITY_TYPES.CHEQUE_CLEARED]: {
    label: 'Cheque cleared',
    tone: 'success',
    sentence: (e) => `Marked cheque ${e.subjectLabel || ''} as cleared.`.replace('  ', ' '),
  },
  [ACTIVITY_TYPES.CHEQUE_BOUNCED]: {
    label: 'Cheque bounced',
    tone: 'danger',
    sentence: (e) => `Marked cheque ${e.subjectLabel || ''} as bounced.`.replace('  ', ' '),
  },
  [ACTIVITY_TYPES.STOCK_ADJUSTED]: {
    label: 'Stock adjusted',
    tone: 'warning',
    sentence: (e) => `Adjusted stock of ${e.subjectLabel || 'an item'}${e.reason ? ` — ${e.reason}` : ''}.`,
  },
  [ACTIVITY_TYPES.STOCK_TRANSFERRED]: {
    label: 'Stock moved',
    tone: 'neutral',
    sentence: (e) => `Moved ${e.subjectLabel || 'stock'} ${e.detail || 'between locations'}.`,
  },
  [ACTIVITY_TYPES.STOCK_TAKE]: {
    label: 'Stock take',
    tone: 'warning',
    sentence: (e) => `Committed a physical count${e.detail ? ` — ${e.detail}` : ''}.`,
  },
  [ACTIVITY_TYPES.PRICE_LIST_CHANGED]: {
    label: 'Prices changed',
    tone: 'warning',
    sentence: (e) => `Changed the ${e.subjectLabel || 'price list'}${e.detail ? ` — ${e.detail}` : ''}.`,
  },
  [ACTIVITY_TYPES.MEMBER_INVITED]: {
    label: 'Person added',
    tone: 'warning',
    sentence: (e) => `Added ${e.subjectLabel || 'somebody'} as ${e.detail || 'a member'}.`,
  },
  [ACTIVITY_TYPES.MEMBER_ROLE_CHANGED]: {
    label: 'Role changed',
    tone: 'warning',
    sentence: (e) => `Changed ${e.subjectLabel || 'a member'} to ${e.detail || 'another role'}.`,
  },
  [ACTIVITY_TYPES.MEMBER_REMOVED]: {
    label: 'Person removed',
    tone: 'danger',
    sentence: (e) => `Removed ${e.subjectLabel || 'a member'} from this business.`,
  },
  [ACTIVITY_TYPES.SETTINGS_CHANGED]: {
    label: 'Settings changed',
    tone: 'neutral',
    sentence: (e) => `Changed ${e.subjectLabel || 'settings'}.`,
  },
  [ACTIVITY_TYPES.RETURN_FILED]: {
    label: 'Return marked filed',
    tone: 'neutral',
    sentence: (e) => `Marked ${e.subjectLabel || 'a return'} as filed.`,
  },
  [ACTIVITY_TYPES.ACC_VOUCHER_POSTED]: {
    label: 'Voucher posted',
    tone: 'neutral',
    sentence: (e) =>
      `Posted ${e.subjectLabel || 'a voucher'}${e.detail ? ` — ${e.detail}` : ''}.`,
  },
  [ACTIVITY_TYPES.ACC_VOUCHER_EDITED]: {
    label: 'Voucher edited',
    tone: 'warning',
    sentence: (e) =>
      `Edited ${e.subjectLabel || 'a voucher'}${e.detail ? ` — ${e.detail}` : ''}.`,
  },
  [ACTIVITY_TYPES.ACC_VOUCHER_CANCELLED]: {
    label: 'Voucher cancelled',
    tone: 'danger',
    sentence: (e) =>
      `Cancelled ${e.subjectLabel || 'a voucher'}${e.reason ? ` — ${e.reason}` : ''}.`,
  },
  [ACTIVITY_TYPES.PERIOD_LOCKED]: {
    label: 'Period locked',
    tone: 'warning',
    sentence: (e) => `Locked ${e.subjectLabel || 'a period'}${e.reason ? ` — ${e.reason}` : ''}.`,
  },
  [ACTIVITY_TYPES.PERIOD_REOPENED]: {
    label: 'Period reopened',
    tone: 'danger',
    sentence: (e) => `Reopened ${e.subjectLabel || 'a locked period'}.`,
  },
};

const UNKNOWN = {
  label: 'Activity',
  tone: 'neutral',
  sentence: (e) => e.detail || 'Something happened.',
};

export function activityDescriptor(type) {
  return DESCRIPTORS[type] || UNKNOWN;
}

/**
 * One entry, rendered.
 *
 * Returns the pieces rather than a string, because the list shows the actor
 * and the sentence in different weights and the CSV export wants them apart.
 */
export function describeActivity(entry) {
  const descriptor = activityDescriptor(entry?.type);
  return {
    label: descriptor.label,
    tone: descriptor.tone,
    text: descriptor.sentence(entry || {}),
    actor: entry?.actorName || entry?.actorPhone || 'Somebody',
  };
}

/**
 * The document shape, built in one place so every caller writes the same
 * fields and a reader can rely on them existing.
 *
 * `subjectLabel` is denormalised deliberately: an activity row that says
 * "Cancelled INV/2026-27/0031" is readable a year later even if that voucher
 * has since been renumbered or the item renamed, and joining to fetch the
 * current name would rewrite history every time the name changed.
 */
export function activityFields({
  type,
  actorUid = null,
  actorName = '',
  actorRole = '',
  subjectId = null,
  subjectLabel = '',
  subjectType = null,
  partyName = '',
  detail = '',
  reason = '',
  amount = null,
}) {
  if (!type) throw new Error('An activity entry needs a type.');
  return {
    type,
    actorUid,
    actorName: String(actorName || '').slice(0, 120),
    actorRole: String(actorRole || ''),
    subjectId,
    subjectType,
    subjectLabel: String(subjectLabel || '').slice(0, 160),
    partyName: String(partyName || '').slice(0, 160),
    detail: String(detail || '').slice(0, 240),
    reason: String(reason || '').slice(0, 240),
    /** Integer paise, or null when the action has no amount. */
    amount: amount === null || amount === undefined ? null : Math.round(Number(amount) || 0),
  };
}

/** The filter chips on the activity screen: coarse groups, not sixteen types. */
export const ACTIVITY_GROUPS = [
  { value: 'all', label: 'Everything', types: null },
  {
    value: 'documents',
    label: 'Documents',
    types: [ACTIVITY_TYPES.VOUCHER_CREATED, ACTIVITY_TYPES.VOUCHER_CANCELLED],
  },
  {
    value: 'money',
    label: 'Money',
    types: [
      ACTIVITY_TYPES.PAYMENT_RECORDED,
      ACTIVITY_TYPES.ACCOUNT_ENTRY,
      ACTIVITY_TYPES.ACCOUNT_TRANSFER,
      ACTIVITY_TYPES.CHEQUE_CLEARED,
      ACTIVITY_TYPES.CHEQUE_BOUNCED,
      ACTIVITY_TYPES.ACC_VOUCHER_POSTED,
      ACTIVITY_TYPES.ACC_VOUCHER_EDITED,
      ACTIVITY_TYPES.ACC_VOUCHER_CANCELLED,
    ],
  },
  {
    value: 'stock',
    label: 'Stock',
    types: [
      ACTIVITY_TYPES.STOCK_ADJUSTED,
      ACTIVITY_TYPES.STOCK_TRANSFERRED,
      ACTIVITY_TYPES.STOCK_TAKE,
    ],
  },
  {
    value: 'admin',
    label: 'People & Settings',
    types: [
      ACTIVITY_TYPES.MEMBER_INVITED,
      ACTIVITY_TYPES.MEMBER_ROLE_CHANGED,
      ACTIVITY_TYPES.MEMBER_REMOVED,
      ACTIVITY_TYPES.PRICE_LIST_CHANGED,
      ACTIVITY_TYPES.SETTINGS_CHANGED,
      ACTIVITY_TYPES.RETURN_FILED,
      /* Locking and reopening a period are administrative acts, not money
         ones: nothing moves, but what may move afterwards changes. */
      ACTIVITY_TYPES.PERIOD_LOCKED,
      ACTIVITY_TYPES.PERIOD_REOPENED,
    ],
  },
];

/**
 * Filter loaded entries in the browser.
 *
 * In the browser and not in the query, because Firestore cannot express "type
 * IN this list AND actor == that AND ordered by time" without a composite
 * index per combination, and the screen is paged anyway. What that costs is
 * stated on the screen: the filter narrows what has been LOADED.
 */
export function filterActivity(entries = [], { group = 'all', actorUid = null, search = '' } = {}) {
  const definition = ACTIVITY_GROUPS.find((entry) => entry.value === group);
  const types = definition?.types || null;
  const needle = String(search || '').trim().toLowerCase();

  return entries.filter((entry) => {
    if (types && !types.includes(entry.type)) return false;
    if (actorUid && entry.actorUid !== actorUid) return false;
    if (needle) {
      const haystack = [
        entry.subjectLabel,
        entry.partyName,
        entry.detail,
        entry.reason,
        entry.actorName,
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/** Distinct actors in the loaded set, for the "who" filter. */
export function actorsIn(entries = []) {
  const seen = new Map();
  entries.forEach((entry) => {
    if (!entry.actorUid) return;
    if (!seen.has(entry.actorUid)) {
      seen.set(entry.actorUid, { uid: entry.actorUid, name: entry.actorName || 'Unknown' });
    }
  });
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}
